import { Archive, Bell, Check, Cpu, KeyRound, Laptop, LayoutGrid, LogOut, Mic, Palette, Server, UserRound, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Page, PageHeader } from "@/components/ui/page";
import { ArchiveList } from "@/components/archive-view";
import { ClaudeAccountSettings } from "@/components/claude-account-settings";
import { EngineSettingsPanel } from "@/components/engine-settings";
import { ForeignAgentsSettings } from "@/components/foreign-agents";
import { MembersPanel } from "@/components/members-dialog";
import { NotificationsSettings } from "@/components/notifications-settings";
import { RemoteMachinesSettings } from "@/components/remote-machines-settings";
import { useAuth, useCan } from "@/lib/auth";
import { systemFamily, THEME_GROUPS, themeName, useTheme, type ResolvedTheme } from "@/lib/theme";
import { useAutoSend, useVoiceSupport } from "@/lib/voice/settings";
import { useWindowsPref } from "@/lib/windows-pref";
import { ROLE_LABELS, type BoardDetail } from "@/lib/types";
import { cn } from "@/lib/utils";
import { SwitchRow } from "@/components/switch-row";

/**
 * Settings: how this device looks, the archive, who can use the board, and the
 * account signed in. A page rather than a dialog, so each part has room and back
 * leaves it like any other view. The theme and the dictation switch are per device
 * (localStorage); Notifications (admins) is stored on the server, so a switch turned
 * off holds on every device, and Claude account (admins) is the host's, through the worker.
 */
export function SettingsView({
  archivedBoards,
  now,
  showTitle,
  onOpenBoard,
  onRestoreBoard,
  onDeleteBoard,
}: {
  archivedBoards: BoardDetail[];
  now: number;
  /** False with the tab layout, whose top bar already names the view. */
  showTitle: boolean;
  onOpenBoard: (boardId: string) => void;
  onRestoreBoard: (detail: BoardDetail) => void;
  onDeleteBoard: (detail: BoardDetail) => void;
}) {
  const { user, logout } = useAuth();
  const isAdmin = useCan("admin");
  const canEdit = useCan("member");
  const voice = useVoiceSupport();

  return (
    <Page>
      <PageHeader title="Settings" showTitle={showTitle} />
      <div className="space-y-5">
        <Section icon={<Palette />} title="Appearance" description="On this device only.">
          <ThemePicker />
        </Section>

        <Section icon={<LayoutGrid />} title="Windows" description="On this device only.">
          <WindowsSwitch />
        </Section>

        {voice.enabled ? (
          <Section icon={<Mic />} title="Dictation" description="On this device only. The same switch as in an agent's chat.">
            <DictationSwitch />
          </Section>
        ) : null}

        {isAdmin ? (
          <Section
            icon={<Bell />}
            title="Notifications"
            description="Turn them on per device; the switches below hold for every device you use."
          >
            <NotificationsSettings />
          </Section>
        ) : null}

        {isAdmin ? (
          <Section icon={<Cpu />} title="AI engine" description="Which CLI agents' conversations run on, and the default models.">
            <EngineSettingsPanel />
          </Section>
        ) : null}

        {isAdmin ? (
          <Section icon={<KeyRound />} title="Claude account" description="Which Claude login agent runs on this host sign in as.">
            <ClaudeAccountSettings />
          </Section>
        ) : null}

        {isAdmin ? (
          <Section icon={<Server />} title="Remote machines" description="SSH hosts agents can run commands on, by name, from this host.">
            <RemoteMachinesSettings />
          </Section>
        ) : null}

        {isAdmin ? (
          <Section
            icon={<Laptop />}
            title="Foreign agents"
            description="Claude Code on your own machines, on the board as agents through board-link."
          >
            <ForeignAgentsSettings />
          </Section>
        ) : null}

        {isAdmin ? (
          <Section icon={<Users />} title="Members" description="Invite people by link and choose what they can do.">
            <MembersPanel />
          </Section>
        ) : null}

        <Section
          icon={<Archive />}
          title="Archive"
          description="Boards taken out of the sidebar. Nothing was deleted — open one to read its cards, or restore it to put it back."
          aside={archivedBoards.length > 0 ? <Badge variant="outline">{archivedBoards.length} archived</Badge> : null}
        >
          <ArchiveList
            boards={archivedBoards}
            now={now}
            onOpen={onOpenBoard}
            onRestore={onRestoreBoard}
            onDelete={onDeleteBoard}
            canRestore={canEdit}
            canDelete={isAdmin}
          />
        </Section>

        <Section icon={<UserRound />} title="Account">
          <div className="flex flex-wrap items-center gap-3">
            <div className="grid size-10 shrink-0 place-items-center rounded-full bg-muted text-[13px] font-semibold">
              {initials(user.displayName)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[14px] font-medium">{user.displayName}</p>
              <p className="truncate text-[12.5px] text-muted-foreground">{user.email}</p>
            </div>
            {user.role ? <Badge variant="outline">{ROLE_LABELS[user.role]}</Badge> : null}
            <Button variant="outline" size="sm" onClick={() => void logout()}>
              <LogOut /> Sign out
            </Button>
          </div>
        </Section>
      </div>
    </Page>
  );
}

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join("") || "?";

function Section({
  icon,
  title,
  description,
  aside,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  aside?: React.ReactNode;
  children: React.ReactNode;
}) {
  const id = `settings-${title.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <section aria-labelledby={id} className="rounded-lg border border-border/70 bg-card p-4 win-sm:p-5">
      <div className="mb-4 flex items-start gap-3">
        <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground [&_svg]:size-4">
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <h2 id={id} className="text-[14px] font-semibold tracking-tight">
            {title}
          </h2>
          {description ? <p className="text-[12.5px] text-muted-foreground">{description}</p> : null}
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}

/**
 * The theme as a segmented control, one row per family (lib/theme.ts THEME_GROUPS):
 * its System (when it has light and dark members), then its themes. Each segment carries a live preview: a real scrap of
 * the app drawn inside `data-theme="…"`, so it is the theme's own tokens and not a
 * picture of them. A System shows its dark and light members split down the middle.
 */
function ThemePicker() {
  const { pref, resolved, setPref } = useTheme();
  const following = systemFamily(pref);
  return (
    <div className="space-y-4">
      {THEME_GROUPS.map(({ family, themes }) => (
        <div key={family.id} role="radiogroup" aria-label={`${family.name} themes`}>
          <p className="mb-1.5 px-0.5 text-[11.5px] font-medium uppercase tracking-wide text-muted-foreground">{family.name}</p>
          <div className="grid grid-cols-2 gap-2 win-sm:grid-cols-4">
            {family.system !== null ? (
              <ThemeOption
                selected={pref === family.system}
                label="System"
                hint={family.hint}
                onSelect={() => setPref(family.system)}
                preview={
                  <span className="grid h-16 grid-cols-2 overflow-hidden rounded-md border border-border/70">
                    <ThemeSwatch theme={family.dark} half="left" />
                    <ThemeSwatch theme={family.light} half="right" />
                  </span>
                }
              />
            ) : null}
            {themes.map((theme) => (
              <ThemeOption
                key={theme.id}
                selected={pref === theme.id}
                label={theme.label}
                hint={theme.hint}
                onSelect={() => setPref(theme.id)}
                preview={
                  <span className="block h-16 overflow-hidden rounded-md border border-border/70">
                    <ThemeSwatch theme={theme.id} />
                  </span>
                }
              />
            ))}
          </div>
        </div>
      ))}
      {following ? (
        <p className="text-[12px] text-muted-foreground">Following the device, which is {themeName(resolved)} right now.</p>
      ) : null}
    </div>
  );
}

function ThemeOption({
  selected,
  label,
  hint,
  preview,
  onSelect,
}: {
  selected: boolean;
  label: string;
  hint: string;
  preview: React.ReactNode;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        "group flex flex-col gap-2 rounded-lg border p-2 text-left transition-colors",
        selected ? "border-primary bg-accent/40 ring-1 ring-primary" : "border-border hover:border-ring/50 hover:bg-muted/40",
      )}
    >
      {preview}
      <span className="flex items-center gap-1.5 px-0.5">
        <span className="flex-1 text-[13px] font-medium">{label}</span>
        {selected ? <Check className="size-3.5 text-primary" /> : null}
      </span>
      <span className="px-0.5 text-[11px] leading-tight text-muted-foreground">{hint}</span>
    </button>
  );
}

/** A thumbnail of the app in one theme: a sidebar strip, a column heading, two cards and a pill. */
function ThemeSwatch({ theme, half }: { theme: ResolvedTheme; half?: "left" | "right" }) {
  return (
    <span data-theme={theme} className="flex h-full w-full overflow-hidden bg-background text-foreground" aria-hidden>
      {half !== "right" ? <span className="h-full w-3 shrink-0 border-r border-border bg-surface" /> : null}
      <span className="flex min-w-0 flex-1 flex-col gap-1 p-1.5">
        <span className="flex items-center gap-1">
          <span className="size-1.5 rounded-full bg-kind-active" />
          <span className="h-1 w-6 rounded-full bg-muted-foreground/60" />
          <span className="ml-auto h-1.5 w-2.5 rounded-full bg-waiting" />
        </span>
        <span className="flex flex-col gap-0.5 rounded-sm border border-border bg-card p-1">
          <span className="h-1 w-4/5 rounded-full bg-foreground/80" />
          <span className="h-1 w-1/2 rounded-full bg-kind-review" />
        </span>
        <span className="flex flex-col gap-0.5 rounded-sm border border-border bg-card p-1">
          <span className="h-1 w-3/5 rounded-full bg-foreground/80" />
          <span className="h-1 w-1/3 rounded-full bg-primary" />
        </span>
      </span>
    </span>
  );
}

function WindowsSwitch() {
  const [on, setOn] = useWindowsPref();
  return (
    <SwitchRow
      label="Windows and tiling"
      hint="Off, this device shows one view at a time with the bottom tabs, as on a tablet or phone. Your windows are kept for when you turn it back on."
      checked={on}
      onChange={setOn}
    />
  );
}

function DictationSwitch() {
  const [autoSend, setAutoSend] = useAutoSend();
  return (
    <div className="space-y-2">
      <SwitchRow
        label="Send dictation when I stop talking"
        hint="Otherwise what you said lands in the box for you to check first."
        checked={autoSend}
        onChange={setAutoSend}
      />
    </div>
  );
}

